import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

// A simple and focused hook to check if the page has been scrolled
const useHasScrolled = () => {
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return hasScrolled;
};

// Header component with a new 'isDark' prop for dynamic color themes
const Header = ({ isDark = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const hasScrolled = useHasScrolled();

  const navigationItems = useMemo(() => [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'About', path: '/aboutpage', icon: 'User' },
    { name: 'Services', path: '/servicespage', icon: 'Briefcase' },
    { name: 'Projects', path: '/projectspage', icon: 'FolderOpen' },
    { name: 'Blog', path: '/blogpage', icon: 'BookOpen' }
  ], []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      setIsOverlayVisible(true);
    } else {
      document.body.style.overflow = 'auto';
      const timer = setTimeout(() => setIsOverlayVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isMenuOpen]);

  const isActivePath = useCallback((path) => location?.pathname === path, [location.pathname]);
  const handleMenuToggle = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);

  const handleContactClick = useCallback(() => {
    handleMenuClose();
    if (location.pathname === '/homepage') {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/contactpage');
      }
    } else {
      navigate('/contactpage');
    }
  }, [location.pathname, navigate, handleMenuClose]);

  const textColor = isDark ? 'text-white' : 'text-neutral-900';
  const textSecondaryColor = isDark ? 'text-neutral-400' : 'text-neutral-600';
  const hoverBg = isDark ? 'hover:bg-neutral-800' : 'hover:bg-neutral-200';
  const hoverTextColor = isDark ? 'hover:text-white' : 'hover:text-black';
  const activeLinkBg = isDark ? 'bg-primary/20' : 'bg-primary/10';
  const activeLinkBorder = isDark ? 'border-primary/40' : 'border-primary/20';
  const scrolledBg = isDark ? 'bg-neutral-900/95' : 'bg-white/95';
  const scrolledBorder = isDark ? 'border-neutral-700' : 'border-neutral-200';

  return (
    <>
      <header
        className={`fixed top-4 left-4 right-4 z-50 transition-all duration-300 rounded-xl
          ${hasScrolled
            ? `${scrolledBg} backdrop-blur-lg border ${scrolledBorder} shadow-lg`
            : 'bg-transparent'
          }
        `}
      >
        <div className="container mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Main Logo */}
            <Link
              to="/homepage"
              className="flex items-center space-x-1 sm:space-x-2 group transition-transform hover:scale-105"
              onClick={handleMenuClose}
              aria-label="Home"
            >
              <div className="relative flex items-center">
                <span className="text-primary font-bold text-2xl sm:text-3xl font-poppins font-semibold">AD</span>
                <div className="absolute -top-1 right-[-8px] w-3 h-3 bg-primary rounded-full animate-pulse"></div>
              </div>
              <div className="hidden min-[320px]:block">
                <h1 className={`text-xl font-brand-headline ${isDark ? 'text-white' : 'text-black'} group-hover:text-primary transition-colors`}>
                  Adrian
                </h1>
                <p className={`text-xs ${textSecondaryColor} font-brand-body -mt-1`}>
                  Developer & Mentor
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2 ${
                    isActivePath(item.path)
                      ? `text-primary ${activeLinkBg} border ${activeLinkBorder}`
                      : `${textColor} ${hoverTextColor} ${hoverBg}`
                  }`}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleContactClick}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={16}
                className={`${isDark ? 'border-neutral-500 text-neutral-200 hover:bg-neutral-800 hover:border-neutral-400' : 'border-neutral-400 text-neutral-700 hover:bg-neutral-200'}`}
              >
                Let's Talk
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleContactClick}
                iconName="Rocket"
                iconPosition="left"
                iconSize={16}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
              >
                Start Project
              </Button>
            </div>

            {/* Mobile Menu Button - Conditionally renders */}
            {!isMenuOpen && (
              <button
                onClick={handleMenuToggle}
                className={`lg:hidden p-2 rounded-lg ${textColor} ${hoverTextColor} ${hoverBg} transition-colors`}
                aria-label="Toggle menu"
              >
                <Icon name="Menu" size={24} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden ${
          isOverlayVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleMenuClose}
      />

      {/* Mobile Menu Container */}
      <div
        className={`fixed inset-y-0 left-0 w-4/5 max-w-[300px] lg:hidden transform transition-transform duration-300 ease-in-out z-50
          ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div
          className={`h-full ${isDark ? 'bg-neutral-900' : 'bg-white'} border-r ${scrolledBorder} shadow-2xl overflow-y-auto`}
        >
          {/* Header inside the mobile menu */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200">
            <Link to="/homepage" onClick={handleMenuClose} className="flex items-center space-x-2">
              <span className="text-primary font-bold text-3xl font-poppins font-semibold">AD</span>
              <div className={`text-lg font-semibold ${textColor}`}>Adrian</div>
            </Link>
            {/* Close button inside the sidebar - ONLY shows the X icon */}
            <button
              onClick={handleMenuClose}
              className={`p-2 rounded-lg ${textColor} ${hoverTextColor} ${hoverBg}`}
              aria-label="Close menu"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Mobile Navigation Links */}
          <div className="px-4 py-6 space-y-4">
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleMenuClose}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActivePath(item.path)
                      ? `text-primary ${activeLinkBg} border ${activeLinkBorder}`
                      : `${textColor} ${hoverTextColor} ${hoverBg}`
                  }`}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>

            <div className={`pt-4 border-t ${scrolledBorder} space-y-3`}>
              <Button
                variant="outline"
                fullWidth
                onClick={handleContactClick}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={18}
                className={`${isDark ? 'border-neutral-500 text-neutral-200 hover:bg-neutral-800 hover:border-neutral-400' : 'border-neutral-400 text-neutral-700 hover:bg-neutral-200'}`}
              >
                Let's Talk
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={handleContactClick}
                iconName="Rocket"
                iconPosition="left"
                iconSize={18}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
              >
                Start Project
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;